import { useSyncExternalStore } from 'react';
import type { NetBasis } from '@shared/calendar';

export type { NetBasis };

export const BASIS_KEY = 'vudget-net-basis';
/** `app_settings` row — this is a reasoning preference, not a device one. */
export const BASIS_DB_KEY = 'net_basis';

/**
 * Which monthly figure the app is quoting.
 *
 * Two true answers to "what do I make a month", differing by 7.69% on a
 * bi-weekly schedule:
 *
 *  - **accrual** — `annual / 12`. Smooth, comparable, the right CSP
 *    denominator and the right basis for long-horizon compounding. The number
 *    you tell someone.
 *  - **guaranteed** — what the ten two-paycheck months actually deliver. The
 *    number to decide against, because it is the one you can count on.
 *
 * ── Why this is not built like the mask ──
 *
 * `useMasked` is the obvious model and the wrong one to copy wholesale. Masking
 * works at the *format* layer: `setMaskFlag` substitutes the string inside
 * `fmtMoney`, so nothing upstream knows or cares, and every figure changes
 * everywhere for free. That trick cannot work here. The basis changes computed
 * values that flow into the projection engine, the budget meter and the chart
 * geometry — swap the label without swapping the input and you get captions
 * that disagree with the bars beside them, which is a worse bug than the one
 * this whole change set out to fix. So the store is the same shape as the
 * mask's, and it is read as *data* rather than applied as a filter.
 *
 * ── Why it persists differently ──
 *
 * The mask is localStorage-only on purpose: whether the phone hides amounts on
 * the train says nothing about the laptop at home. The basis is not
 * device-contextual — it is which set of numbers you are currently reasoning
 * with, and a phone and a laptop disagreeing about that is actively harmful.
 * So D1 is the source of truth (same path as theme, via `usePreferences`) and
 * localStorage is only the first-paint cache.
 */
const read = (): NetBasis => {
  try {
    return localStorage.getItem(BASIS_KEY) === 'guaranteed' ? 'guaranteed' : 'accrual';
  } catch {
    return 'accrual';
  }
};

let basis: NetBasis = read();
const listeners = new Set<() => void>();

const emit = () => listeners.forEach(l => l());

function write(next: NetBasis): void {
  if (next === basis) return;
  basis = next;
  try { localStorage.setItem(BASIS_KEY, next); } catch { /* private mode */ }
  emit();
}

export function setNetBasis(next: NetBasis): void {
  write(next);
}

export function toggleNetBasis(): void {
  write(basis === 'accrual' ? 'guaranteed' : 'accrual');
}

/**
 * Adopt the stored value without treating it as a user action.
 *
 * `usePreferences` calls this once on boot so hydration doesn't immediately
 * echo back to the server as though the toggle had been pressed.
 */
export function hydrateNetBasis(next: NetBasis | undefined | null): void {
  if (next !== 'accrual' && next !== 'guaranteed') return;
  write(next);
}

export const isNetBasis = (v: unknown): v is NetBasis =>
  v === 'accrual' || v === 'guaranteed';

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
const getSnapshot = () => basis;

/**
 * The basis, outside React.
 *
 * The store needs it because the cap is a *calculation*, not a rendering: the
 * effective dials are trimmed to fit whichever income figure is live, and a
 * component reading the flag through a hook can only re-label a total that was
 * already computed against the wrong ceiling. These two exports are what let
 * the store recompute on a flip rather than repaint on one.
 */
export const getNetBasis = (): NetBasis => basis;
export const subscribeNetBasis = subscribe;

export function useNetBasis(): { basis: NetBasis; guaranteed: boolean; toggle: () => void } {
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { basis: value, guaranteed: value === 'guaranteed', toggle: toggleNetBasis };
}

/** Short caption for whichever figure is on screen. Always shown, never a tooltip. */
export const basisLabel = (b: NetBasis, checksPerMonth = 2): string =>
  b === 'accrual' ? 'accrual · annual ÷ 12' : `guaranteed · ${checksPerMonth} paychecks`;
